import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getProductsByCategory } from '@/mocks/products';
import useSEO from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = useMemo(() => (slug ? getCategoryBySlug(slug) : null), [slug]);
  const products = useMemo(() => (slug ? getProductsByCategory(slug) : []), [slug]);

  useSEO({
    title: category ? `${category.name} - 掌中之物精品分类` : '精品分类',
    description: category ? category.description : '探索掌中之物精品分类',
    keywords: category ? `掌中之物${category.name},掌中之物精品,${category.name}选购,掌中之物${category.name}推荐` : '',
    canonicalUrl: `https://szatmkj.cn/category/${slug}`,
    schema: categorySchema,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return <NotFound />;
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 掌中之物`,
    description: category.description,
    url: `https://szatmkj.cn/category/${category.slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://szatmkj.cn' },
        { '@type': 'ListItem', position: 2, name: category.name },
      ],
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">{category.description}</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 shrink-0">
              <h3 className="text-sm font-semibold text-foreground-700 mb-3">所有分类</h3>
              <nav className="space-y-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className="ml-2 text-xs text-foreground-400">({cat.productCount})</span>
                  </Link>
                ))}
              </nav>
            </aside>

            <div className="flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer border border-background-200/70 transition-shadow hover:shadow-lg"
                  >
                    <div className="aspect-square w-full overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {product.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-1 line-clamp-1">
                        {product.name}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold text-accent-600">
                          &yen;{product.price.toLocaleString()}
                        </span>
                        <span className="text-xs text-foreground-400">{product.material}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {products.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-foreground-500">该分类暂无精品，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}